import { useEffect, useState } from 'react';
import { DangerButton } from '../../../components/buttons';
import { useAsyncAction } from '../../../hooks/use-async-action';
import type { ReceivingService } from '../../../services/receiving';

type WaitingNudgeProps = {
  storeId: string;
  sessionId: string;
  service: ReceivingService;
  waitingSince: string;
  readOnly: boolean;
  /** Monotonic count of busy-refused Abandon clears (companion replied
   * rcv_clear_result{busy:true} — LPOS holds the file, session kept alive).
   * Any non-zero value shows the inline "close LiquorPOS" message; the count
   * (not a boolean) mirrors probeRefusedCount so back-to-back refusals edge. */
  abandonBusyCount?: number;
};

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Waiting nudge (spec §10 — waiting_for_lpos > 1h).
 *
 * Client-side timer from waiting_since. Renders nothing until the 1h threshold
 * is crossed; then shows the nudge + Abandon action. Abandon button stays
 * visible after a busy:true refusal (inline "close LiquorPOS" message instead),
 * and after a failed request (inline error — e.g. companion offline 409) so the
 * user can retry.
 */
export function WaitingNudge({
  storeId,
  sessionId,
  service,
  waitingSince,
  readOnly,
  abandonBusyCount = 0,
}: WaitingNudgeProps) {
  const [abandonError, setAbandonError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const { pending: abandoning, run: handleAbandon } = useAsyncAction(
    () => service.abandon(storeId, sessionId),
    { errorLabel: 'receiving: abandon failed', setError: setAbandonError, disabled: readOnly }
  );

  const elapsedMs = now - new Date(waitingSince).getTime();
  const showNudge = elapsedMs >= ONE_HOUR_MS;

  // The nudge is purely time-gated, so without a timer a tab left open before
  // the 1h threshold never re-renders to reveal it — the very tab the nudge is
  // meant to rescue. Schedule one re-render at the threshold; once shown, the
  // 30-min reminder it nests is already past so no further timer is needed.
  useEffect(() => {
    if (showNudge) {
      return;
    }

    const timer = setTimeout(() => setNow(Date.now()), ONE_HOUR_MS - elapsedMs);

    return () => clearTimeout(timer);
  }, [showNudge, elapsedMs]);

  if (!showNudge) {
    return null;
  }

  return (
    <div
      className="mt-4 flex flex-col gap-3 p-4 bg-amber-50 rounded-md border border-amber-200"
      data-testid="waiting-nudge"
    >
      <p className="text-sm font-medium text-amber-900">
        Did you finish receiving in LiquorPOS? If it crashed, restart it and complete the receiving
        — or click Abandon to clear this and start over.
      </p>
      {abandonBusyCount > 0 && (
        <p className="text-sm text-amber-800" data-testid="waiting-nudge-busy">
          Close LiquorPOS completely, then click Abandon again to release the file.
        </p>
      )}
      {abandonError && (
        <p className="text-sm font-medium text-red-700" data-testid="waiting-nudge-error">
          {abandonError}
        </p>
      )}
      {elapsedMs >= 30 * 60 * 1000 && (
        <p className="text-xs text-amber-700" data-testid="waiting-nudge-reminder">
          Still stuck? Abandon ends this receiving and frees the store computer. LiquorPOS may
          already have imported this — check LiquorPOS before starting over.
        </p>
      )}
      <div className="flex justify-end">
        <DangerButton onClick={handleAbandon} disabled={readOnly || abandoning}>
          {abandoning ? 'Abandoning…' : 'Abandon'}
        </DangerButton>
      </div>
    </div>
  );
}
